import { useQuery } from '@tanstack/react-query';

import { diseaseCodeApi } from '@/features/disease-code/api/disease-code.adapter';
import type {
  DiseaseCode,
  DiseaseGroup,
} from '@/features/disease-code/types/disease-code.types';

// One bundle per group so the filter dropdown can render hierarchical
// optgroups without a per-render N+1.
export interface DiseaseFilterGroup {
  group: DiseaseGroup;
  codes: DiseaseCode[];
}

// PRD §3.1 says the filter syncs with disease-code-management. Rather than
// have the dropdown fan out one fetch per group on every keystroke, we wrap
// the listGroups → Promise.all(listCodes) call in a single React Query so
// downstream consumers see one stable cache key.
export function useDiseaseFilterOptionsQuery() {
  return useQuery({
    queryKey: ['questionnaire-design', 'disease-filter-options'] as const,
    queryFn: async (): Promise<DiseaseFilterGroup[]> => {
      const groups = await diseaseCodeApi.listGroups();
      const codeBuckets = await Promise.all(
        groups.map((group) => diseaseCodeApi.listCodes(group.id)),
      );
      return groups.map((group, idx) => ({
        group,
        codes: codeBuckets[idx],
      }));
    },
    // The filter list is stable during an authoring session; refetching on
    // every focus change would re-flash the dropdown.
    staleTime: 5 * 60 * 1000,
  });
}
